import { getEnv } from '@unni/platform'

export const RECAPTCHA_SITE_KEY = getEnv('NEXT_PUBLIC_RECAPTCHA_SITE_KEY')

export type RecaptchaCustomAction =
  | 'email_login'
  | 'sns_login'
  | 'email_signup'
  | 'sns_signup'
  | 'purchase'
  | 'comment'
  | 'reset_password'

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        execute: (token: string, options: { action: RecaptchaCustomAction }) => Promise<string>
      }
      execute: (token: string, options: { action: RecaptchaCustomAction }) => Promise<string>
    }
  }
}

enum RecaptchaManagerErrorType {
  LOAD_TIMEOUT = 'recaptcha_load_timeout',
  EXECUTE_FAILED = 'recaptcha_execute_failed',
  EXECUTE_EXCEPTION = 'recaptcha_execute_exception',
}

/**
 * @description
 * reCAPTCHA Enterprise v3 관리자 클래스입니다.
 *
 * 이 클래스는 싱글톤 패턴으로 구현되어 있으며, 다음과 같은 주요 기능을 제공합니다:
 *
 * 🔧 주요 기능:
 * 1. reCAPTCHA Enterprise 스크립트 동적 로드 (타임아웃에 따른 에러 처리 포함)
 * 2. reCAPTCHA 토큰 생성 및 반환 (실패 시 null 반환으로 graceful degradation)
 * 3. 에러 처리 및 로깅 (TODO: 향후 Datadog 연동 예정)
 * 4. 중복 로드 방지 및 상태 관리
 *
 * 📝 사용법:
 * ```typescript
 * const recaptcha = RecaptchaManager.getInstance();
 * const token = await recaptcha.execute('login_action');
 * ```
 *
 * ⚠️ 주의사항:
 * - 스크립트 로드 실패 시에도 에러를 발생시키지 않고 null을 반환합니다
 * - 토큰 생성 실패 시에도 애플리케이션이 중단되지 않도록 설계되었습니다
 */
class RecaptchaManager {
  private static instance: RecaptchaManager
  private isLoaded = false
  private isLoading = false
  private loadPromise: Promise<void> | null = null
  private hasError = false

  static getInstance() {
    if (!this.instance) {
      this.instance = new RecaptchaManager()
    }
    return this.instance
  }

  async ensureLoaded(): Promise<void> {
    if (this.isLoaded || this.hasError) return
    if (this.loadPromise) return this.loadPromise

    this.isLoading = true
    this.loadPromise = this.loadScript()
    await this.loadPromise

    if (this.isLoaded) {
      this.hideRecaptchaBadge()
    }
  }

  private loadScript(): Promise<void> {
    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}&size=invisible`
      const cleanup = () => {
        this.isLoading = false
        resolve() // 성공/실패 관계없이 resolve
      }

      script.onload = () => {
        this.isLoaded = true
        cleanup()
      }

      script.onerror = () => {
        this.hasError = true
        this.reportError(RecaptchaManagerErrorType.LOAD_TIMEOUT, new Error('Load timeout'))
        cleanup()
      }

      // 로딩이 10초 이상 걸리면 에러 처리
      setTimeout(() => {
        if (this.isLoading) {
          this.hasError = true
          console.error('reCAPTCHA 로드 타임아웃')
          this.reportError(RecaptchaManagerErrorType.LOAD_TIMEOUT, new Error('Load timeout'))
          cleanup()
        }
      }, 10 * 1000)

      document.head.appendChild(script)
    })
  }

  /**
   * @description
   * reCAPTCHA를 실행하여 토큰을 생성합니다.
   * @param action 사용자 지정 액션
   * @returns 토큰 또는 에러 발생 시 null
   */
  async execute(action: RecaptchaCustomAction): Promise<string | null> {
    await this.ensureLoaded()

    // reCAPTCHA가 로드되지 않았으면 null 반환 (에러 없이)
    if (!this.isLoaded || this.hasError) {
      console.warn('reCAPTCHA를 사용할 수 없어 토큰 없이 진행합니다.')
      return null
    }

    // Site Key가 설정되지 않은 경우
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('reCAPTCHA Site Key가 환경변수로 설정되지 않았습니다.')
      return null
    }

    try {
      const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action })
      return token
    } catch (error: any) {
      console.error('reCAPTCHA 실행 실패:', error)
      // 401 에러인 경우 도메인 설정 문제일 가능성
      if (error?.message?.includes('401') || error?.status === 401) {
        console.error('reCAPTCHA 401 에러: 도메인 설정을 확인해주세요.')
        console.error('현재 도메인:', window.location.hostname)
        console.error('Site Key:', RECAPTCHA_SITE_KEY)
      }
      this.reportError(RecaptchaManagerErrorType.EXECUTE_FAILED, error)
      return null // 에러 시에도 null 반환
    }
  }

  private hideRecaptchaBadge(): void {
    const style = document.createElement('style')
    style.textContent = `
      .grecaptcha-badge { 
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `
    document.head.appendChild(style)
  }

  private reportError(type: RecaptchaManagerErrorType, error: any) {
    try {
      // TODO: 추후, datadog log 전송을 추가합니다. @modn
      //ex.  errorReportingService.report(type, error);
      console.error('reCAPTCHA Manager Error:', type, error)
    } catch (reportError) {
      console.error('에러 리포팅 실패:', reportError)
    }
  }
}

export default RecaptchaManager
